import React from 'react';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';

const theme = responsiveFontSizes(createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
    },
    typography: {
        fontFamily: [
            'Open Sans',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
        ].join(','),
    },
    palette: {
        background: {
            paper: '#FFFFFF',
            default: '#FAFAFA',
        },
        primary: {
            light: '#8797D1',
            main: '#697DC6',
            dark: '#464D81',
        },
        secondary: {
            light: '#AD365B',
            main: '#990433',
            dark: '#6B0223',
        },
        info: {
            main: '#4F99E7',
        },
        error: {
            main: '#990433',
        },
        text: {
            primary: '#666666',
        },
        white: {
            main: '#FFFFFF',
        },
        whiteSmoke: {
            main: '#F3F3F3',
        },
        doveGray: {
            main: '#666666',
        },
        darkGray: {
            main: '#D0D0D0',
            dark: '#B0B0B0',
        },
        modalBackdrop: {
            main: 'rgba(230, 230, 230, 0.8)',
        },
        modalHeader: {
            main: '#FAFAFA',
        },
        modalClose: {
            main: '#A5A5A5',
        },
        modalFooter: {
            main: '#CACACA',
        },
        modalText: {
            main: '#2E74B6',
        },
        schoolBorder: {
            main: '#E7E7E7',
            dark: '#DEDEDE',
        },
        schoolTitle: {
            main: '#3F7AB8',
        },
        removeButton: {
            main: '#DC2226',
        },
        footer: {
            main: '#E5E8ED',
        },
        footerTitle: {
            main: 'rgb(74, 74, 86)',
        },
        footerNav: {
            main: 'rgb(106, 106, 106)',
        },
        footerIcon: {
            main: '#A5A5A5',
        },
        footerText: {
            main: 'rgb(153, 153, 153)',
        },
        footerLink: {
            main: 'rgb(102, 102, 102)',
        },
        footerDivider: {
            main: 'rgb(194, 194, 194)',
        },
    },
    transition: '0.3s ease',
    shadow: '0px 2px 4px -1px rgba(0, 0, 0, 0), 0px 4px 5px 0px rgba(0, 0, 0, 0.03), 0px 1px 10px 0px rgba(0,0,0,0.12)',
    paperShadow: '0px 2px 4px -1px rgba(0, 0, 0, 0), 0px 4px 5px 0px rgba(0, 0, 0, 0.03), 0px 1px 10px 0px rgba(0, 0, 0, 0.2)',
    headerHeight: '110px',
    footerHeight: '336px',
    components: {
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    fontSize: 12,
                    textTransform: 'none',
                    backgroundColor: '#4F99E7',
                    borderRadius: 0,
                    padding: '10px 20px',
                    lineHeight: 'normal',
                    '&:hover': {
                        backgroundColor: '#478ED0',
                    },
                    "&.Mui-disabled": {
                        "backgroundColor": '#A5A5A5',
                        "color": '#FFFFFF',
                    },
                },
            },
        },
        MuiPagination: {
            styleOverrides: {
                root: {
                    color: '#FFFFFF',
                },
            },
        },
        MuiPaginationItem: {
            styleOverrides: {
                root: {
                    color: '#FFFFFF',
                    borderRadius: 0,
                    minWidth: '25px',
                    height: '25px',
                    '&.Mui-selected': {
                        color: '#FFFFFF',
                        backgroundColor: '#4F99E7',
                        '&:hover': {
                            backgroundColor: '#4F99E7',
                        },
                    }
                },
                ellipsis: {
                    display: 'none',
                },
                page: {
                    color: '#FFFFFF',
                    backgroundColor: '#A9A9A9',
                    '&:hover': {
                        backgroundColor: '#828282',
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: '#D0D0D0',
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                filledError: {
                    backgroundColor: '#F44336',
                },
                filledSuccess: {
                    backgroundColor: '#4CAF50',
                },
            }
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    borderRadius: 0,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: 12,
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(0, 0, 0, 0.14) !important',
                    }
                },
            },
        },
    },
}));

declare module '@mui/material/styles' {
    interface Theme {
        transition: React.CSSProperties['transition'];
        shadow: React.CSSProperties['boxShadow'];
        paperShadow: React.CSSProperties['boxShadow'];
        headerHeight: React.CSSProperties['height'];
        footerHeight: React.CSSProperties['height'];
    }
    interface ThemeOptions {
        transition: React.CSSProperties['transition'];
        shadow: React.CSSProperties['boxShadow'];
        paperShadow: React.CSSProperties['boxShadow'];
        headerHeight: React.CSSProperties['height'];
        footerHeight: React.CSSProperties['height'];
    }

    interface Palette {
        white: Palette['primary'];
        whiteSmoke: Palette['primary'];
        doveGray: Palette['primary'];
        darkGray: Palette['primary'];
        modalBackdrop: Palette['primary'];
        modalHeader: Palette['primary'];
        modalClose: Palette['primary'];
        modalFooter: Palette['primary'];
        modalText: Palette['primary'];
        schoolBorder: Palette['primary'];
        schoolTitle: Palette['primary'];
        removeButton: Palette['primary'];
        footer: Palette['primary'];
        footerTitle: Palette['primary'];
        footerNav: Palette['primary'];
        footerIcon: Palette['primary'];
        footerText: Palette['primary'];
        footerLink: Palette['primary'];
        footerDivider: Palette['primary'];
    }
    interface PaletteOptions {
        white: PaletteOptions['primary'];
        whiteSmoke: PaletteOptions['primary'];
        doveGray: PaletteOptions['primary'];
        darkGray: PaletteOptions['primary'];
        modalBackdrop: PaletteOptions['primary'];
        modalHeader: PaletteOptions['primary'];
        modalClose: PaletteOptions['primary'];
        modalFooter: PaletteOptions['primary'];
        modalText: PaletteOptions['primary'];
        schoolBorder: PaletteOptions['primary'];
        schoolTitle: PaletteOptions['primary'];
        removeButton: PaletteOptions['primary'];
        footer: PaletteOptions['primary'];
        footerTitle: PaletteOptions['primary'];
        footerNav: PaletteOptions['primary'];
        footerIcon: PaletteOptions['primary'];
        footerText: PaletteOptions['primary'];
        footerLink: PaletteOptions['primary'];
        footerDivider: PaletteOptions['primary'];
    }
}

export default theme;
